<template>
  <div class="bg-red-100 p-4 rounded-md">
    <!-- Tabs -->
    <div class="flex mb-4">
      <button 
        v-for="tab in tabs" 
        :key="tab"
        :class="[
          'px-4 py-2 text-sm font-medium',
          eRezeptStore.activeTab === tab ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        ]"
        @click="eRezeptStore.setActiveTab(tab)"
      >
        {{ tab }}
      </button>
    </div>

    <!-- Form fields -->
    <div class="mb-4">
      <select 
        :value="eRezeptStore.formData.institution" 
        @input="eRezeptStore.updateFormField('institution', $event.target.value)" 
        class="w-full p-2 border border-gray-300 rounded"
      >
        <option disabled value="">Institution</option>
        <option :value="dbStore.selectedOrganization?.uuid">{{ dbStore.selectedOrganization?.name }}</option>
      </select>
    </div>

    <div class="flex mb-4 space-x-4">
      <select 
        :value="eRezeptStore.formData.ausstellendePerson" 
        @input="eRezeptStore.updateFormField('ausstellendePerson', $event.target.value)" 
        class="w-1/3 p-2 border border-gray-300 rounded"
      >
        <option disabled value="">Ausstellende Person</option>
        <option :value="dbStore.selectedPractitioner?.uuid">
          {{ dbStore.selectedPractitioner?.givenName }} {{ dbStore.selectedPractitioner?.surname }}
        </option>
      </select>
      <select 
        :value="eRezeptStore.formData.abzeichnendePerson" 
        @input="eRezeptStore.updateFormField('abzeichnendePerson', $event.target.value)" 
        class="w-1/3 p-2 border border-gray-300 rounded"
      >
        <option disabled value="">Abzeichnende Person</option>
        <option :value="dbStore.selectedPractitioner?.uuid">
          {{ dbStore.selectedPractitioner?.givenName }} {{ dbStore.selectedPractitioner?.surname }}
        </option>
      </select>
      <input 
        :value="eRezeptStore.formData.aktenzeichen" 
        @input="eRezeptStore.updateFormField('aktenzeichen', $event.target.value)" 
        type="text" 
        class="w-1/3 p-2 border border-gray-300 rounded" 
        placeholder="Aktenzeichen" 
      />
    </div>
    
    <div class="flex mb-4 space-x-4">
      <input 
        :value="eRezeptStore.formData.ausstellungsdatum" 
        @input="eRezeptStore.updateFormField('ausstellungsdatum', $event.target.value)" 
        type="date" 
        class="w-1/2 p-2 border border-gray-300 rounded" 
      />
      <select 
        :value="eRezeptStore.formData.ersatzverordnung" 
        @input="eRezeptStore.updateFormField('ersatzverordnung', $event.target.value)" 
        class="w-1/2 p-2 border border-gray-300 rounded"
      >
        <option value="00">ohne Ersatzverordnungskennzeichen (00)</option>
      </select>
    </div>
    
    <div class="flex mb-4 space-x-4">
      <select 
        :value="eRezeptStore.formData.unfallart" 
        @input="eRezeptStore.updateFormField('unfallart', $event.target.value)" 
        class="w-1/3 p-2 border border-gray-300 rounded"
      >
        <option disabled value="">Unfallart</option>
        <option value="Arbeitsunfall">Arbeitsunfall</option>
        <option value="Wegeunfall">Wegeunfall</option>
      </select>
      <input 
        :value="eRezeptStore.formData.unfalltag" 
        @input="eRezeptStore.updateFormField('unfalltag', $event.target.value)" 
        type="date" 
        class="w-1/3 p-2 border border-gray-300 rounded" 
        placeholder="Unfalltag" 
      />
      <input 
        :value="eRezeptStore.formData.unfallbetrieb" 
        @input="eRezeptStore.updateFormField('unfallbetrieb', $event.target.value)" 
        type="text" 
        class="w-1/3 p-2 border border-gray-300 rounded" 
        placeholder="Unfallbetrieb oder Arbeitgebernummer" 
      />
    </div>
    
    <div class="flex mb-4 space-x-2">
      <input 
        v-model.number="eRezeptStore.formData.anzahl" 
        type="number" 
        class="w-20 p-2 border border-gray-300 rounded" 
        placeholder="Anzahl" 
      />
      <input 
        v-model="eRezeptStore.formData.medikament" 
        type="text" 
        class="flex-grow p-2 border border-gray-300 rounded" 
        :placeholder="getPlaceholder"
        :readonly="eRezeptStore.activeTab !== 'Freitextverordnung'"
      />
      <button @click="openModal" class="bg-white p-2 border border-gray-300 rounded hover:bg-gray-100">+</button>
    </div>
    
    <div class="mb-4">
      <textarea 
        :value="eRezeptStore.formData.dosierung" 
        @input="eRezeptStore.updateFormField('dosierung', $event.target.value)" 
        class="w-full p-2 border border-gray-300 rounded" 
        rows="3" 
        placeholder="Dosierungsanweisungen"
      ></textarea>
    </div>
    
    <div class="mb-4">
      <textarea 
        :value="eRezeptStore.formData.abgabehinweise" 
        @input="eRezeptStore.updateFormField('abgabehinweise', $event.target.value)" 
        class="w-full p-2 border border-gray-300 rounded" 
        rows="3" 
        placeholder="Abgabehinweise"
      ></textarea>
    </div>

    <div class="flex mb-4 space-x-4">
      <input 
        :checked="eRezeptStore.formData.mehrfachverordnung" 
        @input="eRezeptStore.updateFormField('mehrfachverordnung', $event.target.checked)" 
        type="checkbox" 
        class="form-checkbox" 
      />
      <span>Mehrfachverordnung</span>
      <input 
        :value="eRezeptStore.formData.mehrfachverordnungAnzahl" 
        @input="eRezeptStore.updateFormField('mehrfachverordnungAnzahl', Number($event.target.value))" 
        type="number" 
        class="w-20 p-2 border border-gray-300 rounded" 
        placeholder="Anzahl" 
      />
      <select 
        :value="eRezeptStore.formData.mehrfachverordnungNenner" 
        @input="eRezeptStore.updateFormField('mehrfachverordnungNenner', $event.target.value)" 
        class="w-1/4 p-2 border border-gray-300 rounded"
      >
        <option disabled value="">Nenner</option>
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
      </select>
      <input 
        :value="eRezeptStore.formData.mehrfachverordnungStart" 
        @input="eRezeptStore.updateFormField('mehrfachverordnungStart', $event.target.value)" 
        type="date" 
        class="w-1/4 p-2 border border-gray-300 rounded" 
        placeholder="Beginn Einlösefrist" 
      />
      <input 
        :value="eRezeptStore.formData.mehrfachverordnungEnde" 
        @input="eRezeptStore.updateFormField('mehrfachverordnungEnde', $event.target.value)" 
        type="date" 
        class="w-1/4 p-2 border border-gray-300 rounded" 
        placeholder="Ende Einlösefrist" 
      />
    </div>

    <div class="flex justify-end mt-4">
      <button @click="createERezept" class="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600">
        E-Rezept erstellen
      </button>
    </div>

    <!-- Modals -->
    <MedicationModal 
      v-if="showMedicationModal"
      @close="showMedicationModal = false"
      @select="selectMedication"
    />
    
    <FreitextModal
      v-if="showFreitextModal"
      @close="showFreitextModal = false"
      @submit="submitFreitextMedikament"
    />

    <DeviceModal
      v-if="showDeviceModal"
      @close="showDeviceModal = false"
      @submit="submitDevice"
    />
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useERezeptStore } from '../../stores/eRezeptStore';
import { useDbStore } from '../../stores/dbStore';
import MedicationModal from './MedicationModal.vue';
import FreitextModal from './FreitextModal.vue';
import DeviceModal from './DeviceModal.vue';

export default {
  name: 'Muster16Form',
  components: {
    MedicationModal,
    FreitextModal,
    DeviceModal
  },
  emits: ['save'],
  setup(props, { emit }) {
    const eRezeptStore = useERezeptStore();
    const dbStore = useDbStore();
    const showMedicationModal = ref(false);
    const showFreitextModal = ref(false);
    const showDeviceModal = ref(false);
    const tabs = ['PZN-Verordnung', 'Freitextverordnung', 'Hilfsmittelverordnung'];

    const getPlaceholder = computed(() => {
      switch (eRezeptStore.activeTab) {
        case 'PZN-Verordnung':
          return 'Medikament (PZN)';
        case 'Freitextverordnung':
          return 'Medikament (Freitext)';
        case 'Hilfsmittelverordnung':
          return 'Hilfsmittel';
        default:
          return 'Medikament';
      }
    });

    const openModal = () => {
      switch (eRezeptStore.activeTab) {
        case 'PZN-Verordnung':
          showMedicationModal.value = true;
          break;
        case 'Freitextverordnung':
          showFreitextModal.value = true;
          break;
        case 'Hilfsmittelverordnung':
          showDeviceModal.value = true;
          break;
      }
    };

    const selectMedication = (medication) => {
      dbStore.setSelectedMedicationPZN(medication.pzn);
      eRezeptStore.updateFormField('medikament', `${medication.handelsname} (${medication.pzn})`);
      showMedicationModal.value = false;
    };

    const submitFreitextMedikament = (medikament, darreichungsform) => {
      dbStore.setMedicationFreitext(medikament, darreichungsform);
      eRezeptStore.updateFormField('medikament', medikament + (darreichungsform ? ` (${darreichungsform})` : ''));
      showFreitextModal.value = false;
    };

    const submitDevice = (deviceData) => {
      dbStore.setDeviceData(deviceData);
      eRezeptStore.updateFormField('medikament', `${deviceData.display} (${deviceData.code})`);
      showDeviceModal.value = false;
    };

    const createERezept = async () => {
      try {
        await eRezeptStore.createERezept();
        emit('save');
      } catch (error) {
        console.error('Error creating E-Rezept:', error);
        // Hier könnten Sie eine Fehlerbehandlung hinzufügen, z.B. eine Benachrichtigung an den Benutzer
      }
    };

    return {
      eRezeptStore,
      dbStore,
      tabs,
      getPlaceholder,
      showMedicationModal,
      showFreitextModal,
      showDeviceModal,
      openModal,
      selectMedication,
      submitFreitextMedikament,
      submitDevice,
      createERezept
    };
  }
};
</script>